// 戦闘解決：プレイヤー → ボス方向のヒット判定。
// 2026-06-11 hit-timeline: ボス→プレイヤーの判定は src/hits.ts (updateHits) へ全面移行。
//   このファイルはプレイヤー→ボス (通常攻撃/カウンター/致命の一撃) の解決のみを持つ。
// Phase 8:
//   - 倒し方は HP 0 のみ。OHK 処刑はなし。
//   - 体幹崩し (staggered) 中の攻撃ボタンは「致命の一撃」発動 (player.ts → executing state)
//     致命の一撃は resolveExecuteHit が処理 (3ヒット分の個別判定)
//   - 通常攻撃 (resolvePlayerAttack) は staggered 中でも反応するが、致命優先のため通常は通らない

import { AIRBORNE_UNHITTABLE_PX } from './awoken_rampage_choreo'
import type { Boss, BossContactMaterial, FxEvent, GameState, Player, PlayerAttackContact, ScreenShake } from './types'
import { BOSS, COMBAT, EXECUTE, EXECUTE_FX, PLAYER, executeReachFor, playerAttackReachFor } from './balance'
import { bossRiseHoldMs } from './boss_rise'
import { PULL, THRUST, pullTimingOn, postExecuteStillSec, lampExecBossDownMs, aliceFreezeMs, lampExecLightLeadSec, lunaExecLampOn, lunaExecPierceOn, lunaExecSwap3On, lunaExecSwapOn, thrustTimingOn } from './luna_exec_swap'
import {
  isHeavyComboStep,
  isInCounterWindow,
  isPlayerAttackActive,
} from './player'
import { staggerBoss } from './boss'
import { bossPostureGraceSec, econ } from './econ'
import { combo3rdBarOn, combo3rdLockSec, combo3rdMul } from './player_combo3_power'
import { econBaseGainMul } from './econ_baseline'
import { roosterRushPostureGainMul } from './rooster_rush'
import { executeSpacingMode } from './execute_spacing'
import { swingFacingLocked } from './swing_facing_trial'
import { act2BlinkUntouchable } from './exec_act2_blink'
import { execNormalWoundPoint, execV2Sfx, execWoundPoint, finisherProfileFor, finisherV2On } from './luna_finisher'

export type CombatOutcome = {
  player: Player
  boss: Boss
  sfx: GameState['sfx']
  fx: FxEvent[]
  hitStopMs: number
  shake: ScreenShake | null
  // 致命ヒットがボスを撃破したか (HP 0)。 トドメ専用 FX のトリガ。
  lethal: boolean
  playerAttackContact: PlayerAttackContact | null
}

const NO_OUTCOME = (player: Player, boss: Boss): CombatOutcome => ({
  player, boss, sfx: [], fx: [], hitStopMs: 0, shake: null, lethal: false, playerAttackContact: null,
})

// ボスのガード (待機中に主人公の斬撃を受ける) は全ボスで廃止 (2026-09-29 ユーザー「全ボス一律で外す」)。
//   待機中も通常の命中 (else 枝) になる。DEV ?bossGuard=on で旧の受けに戻して比べられる。
let bossGuardEnabled = false
export function getBossGuardEnabled(): boolean { return bossGuardEnabled }
export function setBossGuardEnabled(v: boolean): void { bossGuardEnabled = v }

function contactMaterialFor(boss: Boss, guarded: boolean): BossContactMaterial {
  if (guarded) return boss.meleeGuard === 'veil' ? 'veil' : 'weapon'
  return boss.contactMaterial ?? 'flesh'
}

// ボス中心固定の火花をやめ、刃が手前側の表面へ届いた位置に寄せる。
// 見た目だけの座標で、当たり判定やboss.xは変更しない。
function playerContactPoint(player: Player, boss: Boss): { x: number; y: number; dir: -1 | 1 } {
  const dx = boss.x - player.x
  const dir: -1 | 1 = dx >= 0 ? 1 : -1
  const inset = Math.min(54, Math.max(28, Math.abs(dx) * 0.32))
  return { x: boss.x - dir * inset, y: boss.y - 58, dir }
}

// ========== 部屋制 (2026-06-11 協議) ==========
// HPバーは roomCount 個の部屋に区切られ、 部屋の境界 (床) は致命でしか越えられない。
//   床 = maxHp - 部屋サイズ × (閉じた部屋数+1)。 最後の部屋の床は 0。
// 部屋ごとの HP 取り分 (room順・正規化済・合計1)。 roomHpFractions が roomCount と一致すれば採用、
//   それ以外は均等割り (従来)。 案X (2026-06-13)。
function roomFractions(boss: Boss): number[] {
  const n = Math.max(1, boss.roomCount)
  const f = boss.roomHpFractions
  if (f && f.length === n) {
    const sum = f.reduce((a, b) => a + b, 0)
    if (sum > 0) return f.map((x) => x / sum)
  }
  return Array(n).fill(1 / n)
}

// 本物のSEKIRO型 (2026-07-15 移行): HPは「部屋の境界」ではなく段階ごとに満タンへ戻る独立バーの
//   現在値でしかないため、 床は常に0 (幕の途中でHPが門になることは無い)。
// 🔴 「忍殺の扉は体幹崩しのみ」は 2026-07-16 Part1 で古くなった: 扉は体幹満タン ＊または＊ HP床
//   (chipFloor) の2条件で開く = 下の vitalityEmpty。 台帳 docs/decision-ledger.md「本物のSEKIRO化」。
export function currentRoomFloor(_boss: Boss): number {
  return 0
}

// 通常攻撃/反撃で削れる下限。 常に 1 で止まる (トドメは必ず致命 = finisher-lock 継承)。
export function chipFloor(boss: Boss): number {
  return Math.max(1, currentRoomFloor(boss))
}

// 段階 (phase) の満タンHP。 roomHpFractions (旧: 部屋ごとのHP配分) を「段階ごとのバーサイズ」
//   として読み替える (2026-07-15 移行 D5)。 範囲外の phaseIndex は末尾の段階にクランプ。
function phaseFullHp(boss: Boss, phaseIndex: number): number {
  const fr = roomFractions(boss)
  const idx = Math.min(Math.max(0, phaseIndex), fr.length - 1)
  return boss.maxHp * fr[idx]!
}

// ===== 幕 (act) のヘルパー (combat-feel-v2 §2-3) =====
// 幕 = HPバー1本ぶん (UI上の呼称。 コードは room 系の既存名を継続)。
export function roomSize(boss: Boss): number {
  // 現在の部屋の満タンHP (不均等なら部屋ごとに違う)。 均等なら maxHp/roomCount と一致。
  return phaseFullHp(boss, boss.executesTaken)
}

// 現在の幕の HP 残量率 (床=0 / 幕の満タン=1)。 HUD の幕HPバーと体幹回復スケーリングが使う。
export function roomHpFraction(boss: Boss): number {
  const size = roomSize(boss)
  if (size <= 0) return 0
  const frac = (boss.hp - currentRoomFloor(boss)) / size
  return Math.max(0, Math.min(1, frac))
}

// 体幹回復の幕HP比例 (SEKIROの核心): 幕HP満タン→1.0 / 幕の底→floorMul (ほぼ停止)。
//   「先に削って体幹を固定し、 弾きで割る」の順序を作る。 旧 POSTURE_ASSIST (蓄積側の
//   低HP倍率) はこれに一本化して廃止 (調整 knob を1つにする)。
//
// 🔴 2026-07-29: **直線から段階へ変更。** 出所 = SEKIRO 公式マニュアル
//   「敵の体力は体幹の回復速度に結び付いている ―― 体力が低いほど体幹の回復が遅くなる」
//   + Fextralife の実測表 (HP 100-75%→回復100% / 75-50%→66% / 50-25%→33% / 25%以下→ほぼ停止)。
//   従来の直線は **HP残25%でもまだ回復29%** で、体幹が固定されるのがバーの底だけだった。
//   実測 (tests/combat_sim/economy_probe.test.ts) で全4体が「HP残2〜27%まで削ってから崩れる」
//   = 事実上のHP削り切りゲームになっていた原因のひとつ。
//   ⚠️ 戻し方: BOSS.postureRecoveryCurve を null にすると従来の直線へ戻る。
export function bossPostureRecoveryMult(boss: Boss): number {
  const f = BOSS.postureRecoveryFloorMul
  const hp = roomHpFraction(boss)
  // 2026-10-08 指数の曲線 (戴冠獣の比較の段 c だけ・econ.ts postureRecoveryExp): 体力が減るほど急に戻りにくくなる。
  const k = econ().bossPools?.[boss.id]?.postureRecoveryExp
  if (k !== undefined) return Math.max(f, Math.exp(-k * (1 - hp)))
  const curve = econ().bossPools?.[boss.id]?.postureRecoveryCurve ?? BOSS.postureRecoveryCurve
  if (!curve) return f + (1 - f) * hp          // 従来の直線 (戻し用)
  for (const [minHpFrac, mult] of curve) {
    if (hp >= minHpFrac) return mult
  }
  return f                                      // 最下段 = ほぼ停止
}

/**
 * こちらの行動でボスの体幹が溜まる量の、ボスごとの個性の倍率 (2026-10-10・src/econ_baseline.ts の gainMul)。覚醒王だけ 1.3・ほかは 1。
 *   弾き (通常・致命を弾いた時)・殴り・見切り反撃・弾き返した飛び道具の着弾の、溜まる量すべてに掛ける。DEV ?econBase=0|1 では 1。
 *   ⚠️ 体幹の崩れ判定 (満タン) の見た目 = boss_hud_view.ts breakReadyRatio にも同じ倍率を渡す (render.ts)。
 */
export function bossPostureGainMul(boss: { id: string }): number {
  return econBaseGainMul(boss.id) * roosterRushPostureGainMul(boss)
}

// パリィで溜まる量の幕の体力による倍率 (2026-10-08・戴冠獣の比較の段 c だけ・econ.ts parryGainHpExp)。
//   未指定のボスは常に 1 (今までと同じ)。幕の体力が減るほど指数で増える (弾く人ほど得をする側だけに掛ける)。
export function parryGainHpMul(boss: Boss): number {
  const g = econ().bossPools?.[boss.id]?.parryGainHpExp
  return g === undefined ? 1 : Math.exp(g * (1 - roomHpFraction(boss)))
}

function shakeFrom(spec: { amplitude: number; durationMs: number }, time: number): ScreenShake {
  const dur = spec.durationMs / 1000
  return {
    remainingSec: dur,
    amplitude: spec.amplitude,
    startAmplitude: spec.amplitude,
    totalDur: dur,
    startSec: time,
  }
}

export function resolvePlayerAttack(player: Player, boss: Boss, time: number): CombatOutcome {
  const out = NO_OUTCOME(player, boss)
  if (boss.state === 'blink') return out  // 宵渡り中 (消えている) は当たらない
  if (act2BlinkUntouchable(boss)) return out  // 介錯の幕2の頭の瞬間移動 (開発版 ?execAct2Open=blink) も同じ
  if (boss.postExecuteInvincMs > 0) return out  // 致命完了後の被弾猶予中
  if (!isPlayerAttackActive(player)) return out
  if (player.attackHitConsumed) return out

  // 突進カウンター中は attackIsCounter フラグで確定 (counterWindow の自然減衰と独立)
  const isCounter = player.attackIsCounter || isInCounterWindow(player)

  // 攻撃レンジ: カウンター時は突進ランスで伸びるため拡張
  const reach = isCounter ? PLAYER.counterThrustRange : playerAttackReachFor(boss.bodyHalfWidthPx)
  const dist = Math.abs(boss.x - player.x)
  if (dist > reach) return out
  // DEV ?swingFacing=lock の比較版だけ: ボスの中心が背中側なら当たらない (既定は向きを見ない)
  if (swingFacingLocked() && (boss.x - player.x) * (player.facing === 'right' ? 1 : -1) < 0) return out
  // 大暴れ第5案: 高く跳んでいる間のボスには刃が届かない (高さを持つのはこの技の間だけ)。
  if ((boss.altitudePx ?? 0) > AIRBORNE_UNHITTABLE_PX) return out

  // P2 経済 (ブループリント §4.2「技後の隙 (recovery) と崩し中だけ、刃が通る」):
  //   - カウンター (見切り反撃): 定義上 recovery への攻撃 → 常に生身 (guard 無視)。
  //   - open (recovery/staggered): 攻め時 → 体幹が普段より多く溜まる (econ().bossPostureGainOpen・v2 は 10 対 普段 4)。
  //       HP は econ().bossHpDamageOpen (v2 は 2 = 普段の 2 と同じ。「通常より太い HP」は v1 の 3 の時の話・10-14 に直した)。
  //       ⚠️ ボスの構え (isGuarded) は bossGuardEnabled=false で止まっているので、いまの「開き」の得は体幹と重い手応え (hitstop deep) だけ。
  //   - idle + meleeGuard weapon/veil: 構えで受ける → HP0・体幹だけ固める (居座り殴りに旨味なし)。
  //   - telegraph/active/comboGap/beatGap (= 攻撃モーション中): else 枝で通常ダメージが通る。
  //       振りかぶり(溜め)を読んで差し込めるのはモーション理解者への報酬。 ボスは hyperarmor で
  //       中断しない (被弾しても攻撃は出る) ので差し込みにリスクが成立する = SEKIRO 系の標準挙動
  //       (ユーザー裁定 2026-06-14)。 ⚠️ 全ガードボス共通 (花嫁veil / 騎士・処刑人・剣舞・姫weapon)。
  //   - none (亡霊/王): 従来通り chip (HP2) = 序盤は素直に殴れる進行カーブ。
  const isOpen = boss.state === 'recovery' || boss.state === 'staggered'
  const isGuarded = bossGuardEnabled && !isCounter && boss.state === 'idle' && boss.meleeGuard !== 'none'
  const contactPoint = playerContactPoint(player, boss)
  let damage: number
  let postureGain: number
  if (isCounter) {
    damage = econ().bossHpDamageCounter
    postureGain = COMBAT.counterAttack.bossPostureGain
  } else if (isOpen) {
    damage = econ().bossHpDamageOpen
    // G-5機構: 開き (recovery/staggered) を読んで刺した時だけ体幹を太く。
    //   量は econ().bossPostureGainOpen で、 balance.ts の v1 値 (8) とは別。 現在値 =
    //   docs/generated/econ-current-values.md (自動生成)。
    postureGain = econ().bossPostureGainOpen
  } else if (isGuarded) {
    damage = 0
    postureGain = COMBAT.playerAttack.guardPostureGain
  } else {
    damage = COMBAT.playerAttack.bossHpDamage
    postureGain = COMBAT.playerAttack.bossPostureGain
  }

  // 連撃の締め (3段目) の強さの比較版 (DEV ?combo3rd=<百分率>・指定なし = 100 = 全段同じ = 配布版)。
  //   3段目の通常命中 (浅い・深い) の体力ダメージと体幹ダメージに同じ倍率を掛ける。見切り反撃・受けの枝は対象外。
  const heavyNormalHit = !isCounter && !isGuarded && isHeavyComboStep(player)
  if (heavyNormalHit) {
    const m = combo3rdMul()
    damage *= m
    postureGain *= m
  }

  // 削りは 1 で止まる (トドメは必ず致命)。 部屋の境界という概念はもう無い (本物のSEKIRO型)。
  const floor = chipFloor(boss)

  // 欲張り分岐 (2026-08-31 ユーザー裁定「Aで」): telegraph 中に greedSwitch 持ち攻撃へこの一撃が
  //   ダメージ成立したら回数を積む (proximityHeldMs と同じ役割分担＝カウントはここ、切替は boss.ts の
  //   telegraph ケース)。preActiveLunge 開始後 (急接近フェーズ) はカウント対象外＝
  //   proximitySwitch 旧実装の atMs 相当の線を、rushStartMs (chargeMs-preActiveLungeMs) で引く。
  //   正本 = docs/candidates/2026-08-31-executioner-greed-branch-design.md
  let greedHitCount = boss.greedHitCount
  const gs = boss.currentAttack?.greedSwitch
  if (gs && damage > 0 && boss.state === 'telegraph') {
    const beat = boss.currentString?.beats[boss.beatIndex]
    const chargeMs = beat?.chargeOverrideMs ?? boss.currentAttack!.chargeMs
    const preActiveLungeMs = boss.currentAttack!.preActiveLungeMs
    const rushStartMs = preActiveLungeMs != null ? Math.max(0, chargeMs - preActiveLungeMs) : Infinity
    const elapsedMs = boss.stateElapsed * 1000
    if (elapsedMs < rushStartMs) greedHitCount = boss.greedHitCount + 1
  }

  out.boss = {
    ...boss,
    hp: Math.max(floor, boss.hp - damage),
    // ②本物のSEKIRO化 (2026-07-16): ゲインは幕HP率でスケールしない (gain-wall 撤去)。
    //   崩しやすさは「HP減少に伴う回復の鈍さ (bossPostureRecoveryMult)」1本で作る
    //   (体力が高いほど体幹回復が速い=SEKIRO本家のHP↔体幹カップリング)。
    posture: Math.min(boss.maxPosture, boss.posture + postureGain * bossPostureGainMul(boss)),
    // 体幹ゲインがあった時は回復停止タイマーをリセット (v2 経済。v1 は 0=無効)
    postureRegenLockSec: postureGain > 0
      ? Math.max(boss.postureRegenLockSec ?? 0, bossPostureGraceSec(boss.id))
      : (boss.postureRegenLockSec ?? 0),
    recentHitAgesMs: [...boss.recentHitAgesMs, 0],   // 割り込み反撃の素材 (combat-feel-v2 §4.1)
    greedHitCount,
  }
  out.player = {
    ...player,
    attackHitConsumed: true,
    counterWindow: isCounter ? 0 : player.counterWindow,
  }

  // 演出: counter 中は大きく / 受け (guard) は刃が弾かれる / それ以外は通常
  if (isCounter) {
    out.playerAttackContact = { kind: 'counter', material: contactMaterialFor(boss, false), ...contactPoint }
    out.sfx = ['counter_hit']
    out.fx = [
      { kind: 'hit_spark', x: contactPoint.x, y: contactPoint.y, t: time, color: 'pink' },
      { kind: 'counter_burst', x: contactPoint.x, y: contactPoint.y, t: time },
    ]
    out.hitStopMs = COMBAT.counterAttack.hitStopMs
    out.shake = shakeFrom(COMBAT.counterAttack.shake, time)
  } else if (isGuarded) {
    // 受け (HP0): 振りの whoosh は鳴るが命中音は出ない (=「効いてない」が耳でも分かる)。
    //   専用の金属音/布の音は sound-dept へ handoff。 視覚は guard_spark で受けを伝える。
    const material = boss.meleeGuard === 'veil' ? 'veil' : 'weapon'
    // 受け = 振りの whoosh + 専用の弾かれ音 (guard)。 命中 (else 枝) の hit_boss=ドスッ と対比させ、
    //   「効いてない (弾かれた)」が耳で即分かるようにする (③⑤)。 視覚は guard_spark + ガード brace 反応。
    out.playerAttackContact = { kind: 'guarded', material, ...contactPoint }
    out.sfx = ['guard']
    out.fx = [{ kind: 'guard_spark', x: contactPoint.x, y: contactPoint.y, t: time, material }]
    out.hitStopMs = Math.round(COMBAT.playerAttack.hitStopMs * 0.5)  // 弾かれる手応え (浅め)
    out.shake = shakeFrom({ amplitude: 1, durationMs: 70 }, time)
  } else {
    const kind = isOpen ? 'deep' : 'shallow'
    const material = contactMaterialFor(boss, false)
    out.playerAttackContact = { kind, material, ...contactPoint }
    // 2026-07-19 ユーザー裁定: 通常命中音は花嫁作業時までの `hit_boss` へ戻す。
    //   2026-07-17〜18 に王 (flesh_chain) だけ実素材版 `hit_boss_blade_hide_*` へ差し替えていたが、
    //   実機で「直近までの音と違う」と判定された。 素材差し替えの試行 (フィードバック1〜6) はここで打ち切り。
    //   ★材質 (material) は接触FX側でまだ使うので残す。 音だけ共通へ戻す。
    //   ★振り音 (attack_swing) は 2026-07-18 に「振り始めで鳴らす」方式へ移したまま = ここでは鳴らさない
    //     (命中時に足すと二重発火する)。
    //   `hit_boss_blade_hide_*` のカタログ定義と wav は削除せず残す (資産は消さない・再採用の余地)。
    // 連撃の締め (3段目) は命中の止まり・揺れ・音を一段重くする (ダメージ・体幹は全段同じ。DEV ?combo3rd で3段目だけ変えて比べられる)。
    const heavy = isHeavyComboStep(player)
    out.sfx = [heavy ? 'hit_boss_heavy' : 'hit_boss']
    out.fx = [{ kind: 'hit_spark', x: contactPoint.x, y: contactPoint.y, t: time, color: 'pink' }]
    // 深い一撃だけ接触の抵抗を長くする。ダメージ/攻撃時間/キャンセル窓は不変。
    out.hitStopMs = heavy
      ? (kind === 'deep' ? COMBAT.playerAttack.heavyDeepHitStopMs : COMBAT.playerAttack.heavyHitStopMs)
      : kind === 'deep'
        ? COMBAT.playerAttack.deepHitStopMs
        : COMBAT.playerAttack.hitStopMs
    out.shake = shakeFrom(heavy
      ? (kind === 'deep' ? { amplitude: 4, durationMs: 130 } : { amplitude: 2.5, durationMs: 95 })
      : kind === 'deep'
        ? { amplitude: 3, durationMs: 110 }
        : { amplitude: 1.5, durationMs: 75 }, time)
  }

  // 接触の意味を攻撃者側にも保持する。戦闘stateや受付時間は変えず、render/Spineが
  // 同じ突きの「弾かれ・浅い戻り・刺さって抜く戻り」だけを選べるようにする。
  out.player = { ...out.player, attackContactKind: out.playerAttackContact.kind }

  // 体幹崩しの瞬間 (stagger 直前)。 ガード削り(ガード上から殴る)では崩さない (2026-06-29 ユーザー裁定)。
  //   開き際/カウンター/通常(非ガード)の攻撃は従来通り崩せる。 ガード時は体幹が満タンでも崖っぷち止まり。
  // ②本物のSEKIRO化 (2026-07-16 Part1): 致命は体幹満タン ＊または＊ 体力ゼロ (床=chipFloor) の
  //   どちらでも開く (SEKIRO本家の3条件のうち2つ・ステルスは無し)。 protectFromDeath (亡霊) では
  //   HP枯渇では開かない (無敵仕様・体幹満タンのみ従来どおり有効)。
  const vitalityEmpty = !isGuarded && !out.boss.protectFromDeath && out.boss.hp <= chipFloor(out.boss)
  if (!isGuarded && out.boss.state !== 'staggered'
    && (out.boss.posture >= out.boss.maxPosture || vitalityEmpty)) {
    out.boss = staggerBoss(out.boss)
    out.sfx = [...out.sfx, 'stagger']
    out.shake = shakeFrom(COMBAT.stagger.shake, time)
  }

  // 連撃3段目の得 (2026-10-12 配布版の既定へ・DEV ?combo3rd=off で切・連撃3段目の得の比較版 (2026-10-12・DEV ?combo3rd=lock / &combo3rdBar=1・指定なし = 何もしない = 配布版)。combo3rdBar=1 の白い残りは DEV だけ)。
  //   条件は上の倍率と同じ (3段目の通常命中・見切り反撃と受けは対象外)。記録 = docs/research/2026-10-12-combo3-lock-bar-status.md。
  if (heavyNormalHit) {
    const act = boss.executesTaken
    // 案1: 体幹の戻りの猶予をその一撃だけ長くする。崩れた一撃では長くしない (崩れ中は戻らない・致命の後へ持ち越さない)。
    const holdSec = combo3rdLockSec()
    if (holdSec > 0 && postureGain > 0 && out.boss.state !== 'staggered') {
      out.boss = { ...out.boss, postureRegenLockSec: Math.max(out.boss.postureRegenLockSec ?? 0, holdSec) }
      out.fx = [...out.fx, { kind: 'posture_hold', t: time, act, sec: holdSec }]
    }
    // 案2: 削った分の白い残り (体力・体幹の両方)。
    if (combo3rdBarOn() && (out.boss.hp < boss.hp || out.boss.posture > boss.posture)) {
      out.fx = [...out.fx, { kind: 'heavy_bar_ghost', t: time, act,
        hpFrom: boss.hp, hpTo: out.boss.hp, postureFrom: boss.posture, postureTo: out.boss.posture }]
    }
  }

  return out
}

// 致命の一撃: executing state 中のヒット判定。
// player.executeHitIndex が示す現在のヒット番号で個別にダメージ適用。
// 各フェーズ (240ms) 内の active 窓 (60-130ms) で 1 回だけヒット。
// 最終ヒット (hitIndex === EXECUTE.hits - 1) ではボスの stagger を強制終了し
// 「致命の一撃でフィニッシュした」感を出す (残り stagger 時間を消費しない)。
export function resolveExecuteHit(player: Player, boss: Boss, time: number): CombatOutcome {
  const out = NO_OUTCOME(player, boss)
  if (player.state !== 'executing') return out
  if (player.attackHitConsumed) return out

  const isFinisher = player.executeVariant === 'finisher'
  const isFinalHit = player.executeHitIndex === EXECUTE.hits - 1
  // 比較版 ?execGap の時だけ光を剣先の接点へ。寄せた後に光がボスの胴の中で出る件 docs/research/2026-10-10-exec-gap-status.md
  const fxPos = executeSpacingMode() !== 'off' ? playerContactPoint(player, boss) : { x: boss.x, y: boss.y - 50 }

  // フィニッシャー致命は「単発」(Codex 監査 A): 中間ヒット(F0〜F10 の溜め保持)では判定を出さない。
  //   見た目1発の深い刺し = 判定も最終ヒット(F17≈567ms)1発に集約。 counter_hit / 中間ダメージは出さない。
  if (isFinisher && !isFinalHit) return out
  // 入れ替えの比較 (DEV ?lunaExec=swap・src/luna_exec_swap.ts): 普段の致命も今の止めの「深く一突き」の絵なので、当たりは3発目の1回だけ。
  //   3発目は段階を満タンへ戻す (下の決まりのまま) ので、前の2発を出さなくても残る体力は同じ。
  const swap = lunaExecSwapOn()
  if (swap && !isFinalHit) return out

  const elapsedMs = player.stateElapsed * 1000
  // 直した版の普段の致命 (DEV ?lunaExec=swap2): 一突きの当たりは THRUST.hitMs の1回 (3発目の窓は使わない)。
  if (thrustTimingOn(player)) {
    if (elapsedMs < THRUST.hitMs || elapsedMs >= THRUST.hitMs + THRUST.hitWindowMs) return out
  } else if (pullTimingOn(player)) {
    // 両手で刺して引き抜く版 (配布版・指定なし): 刺す瞬間 PULL.hitMs の1回
    if (elapsedMs < PULL.hitMs || elapsedMs >= PULL.hitMs + PULL.hitWindowMs) return out
  } else {
    const phaseStartMs = player.executeHitIndex * EXECUTE.perHitDurationMs
    const inPhaseMs = elapsedMs - phaseStartMs
    if (inPhaseMs < EXECUTE.perHitActiveStartMs || inPhaseMs >= EXECUTE.perHitActiveEndMs) {
      return out
    }
  }

  // 致命中はリーチ広め (体幹崩し位置から微妙に離れていても届くように)。
  //   フィニッシャーは「実行中はターゲットへ確定命中」(Codex 監査 B): 空振り判定をしない。
  const dist = Math.abs(boss.x - player.x)
  if (!isFinisher && dist > executeReachFor(boss.bodyHalfWidthPx)) return out

  // フィニッシャー致命 (単発・確定撃破): HP を 0 に、 lethal 確定。 stagger を強制終了。
  //   hitStop は二重掛けせず finisherHitStopMs 単一 (Codex 監査 D) → この後 pullTimeScale の魂吸収スローへ。
  if (isFinisher) {
    let bossNext: Boss = { ...boss, hp: 0 }
    if (bossNext.state === 'staggered') {
      bossNext = { ...bossNext, state: 'idle', stateElapsed: 0, posture: 0, currentAttack: null }
    }
    out.boss = bossNext
    out.player = { ...player, attackHitConsumed: true }
    out.lethal = true
    // 新しい版 (DEV ?lunaFinisher=new・src/luna_finisher.ts): 白で隠さず刺した姿を止めて見せる。止め = freezeMs・揺れは止めの頭だけ・
    //   光は刺し傷の小さな灯だけ (描画 src/luna_finisher_fx.ts)・音は刺す音1本 (振りの音を重ねない)。当たり・撃破の決まりは同じ。
    if (finisherV2On()) {
      const prof = finisherProfileFor(boss.id)
      out.sfx = ['execute']
      out.fx = []
      out.hitStopMs = prof.freezeMs
      out.shake = shakeFrom(prof.shake, time)
      return out
    }
    // 入れ替えの比較: 止めはアリスの技を手本にした駆け抜け。光は src/luna_alice_fx.ts が時計から描く (白い閃光・桃の火花は出さない)。
    //   止めの間に裂け目が順に開く・音は裂ける音1本 (振りの音を重ねない)。
    if (swap) {
      // 重みを足した版 (?lunaExec=swap3): 止めを長く・揺れを重く・音は低い芯と踏ん張りの滑り、裂ける音は1本ずつ間を空ける。
      const heavy = lunaExecSwap3On()
      // 一突きの版 (DEV ?lunaExec=pierce): 刺した瞬間 0.1 秒止まる・音は一突きの1本 (裂ける音は重ねない)・揺れは小さく (静かな間へ繋ぐ)。
      if (lunaExecPierceOn()) {
        out.sfx = ['execute']
        out.fx = []
        out.hitStopMs = aliceFreezeMs()
        out.shake = shakeFrom({ amplitude: 4, durationMs: 120 }, time)
        return out
      }
      out.sfx = [heavy ? 'luna_alice_cuts3' : 'luna_alice_cuts']
      out.fx = []
      out.hitStopMs = aliceFreezeMs()
      out.shake = shakeFrom(heavy ? { amplitude: 8, durationMs: 240 } : { amplitude: 5, durationMs: 160 }, time)
      return out
    }
    out.sfx = ['execute', 'attack_swing']
    out.fx = [
      { kind: 'hit_spark', x: fxPos.x, y: fxPos.y, t: time, color: 'pink' },
      { kind: 'execute_flash', x: fxPos.x, y: fxPos.y, t: time },
    ]
    out.hitStopMs = EXECUTE_FX.finisherHitStopMs
    out.shake = shakeFrom({ amplitude: 12, durationMs: 340 }, time)
    return out
  }

  // --- 以下は通常致命 (executeVariant='normal') の 3 ヒット処理 ---
  const damage = EXECUTE.hitDamages[player.executeHitIndex] ?? 13

  // 段階制 (2026-07-15移行 A2・本物のSEKIRO型): 通常致命は「段階を1つ閉じて次の段階の
  //   フルバーへ戻す」もの。 中間ヒットは 1 より下へ削らず、 最終ヒットで HP を次段階の
  //   満タンへ復帰させる (床へスナップして終わる、ではない)。 最後の段階 (次段階が無い) は
  //   finisher 変種が撃破を担うため、 通常致命が lethal になることは無い。
  const rawHp = boss.hp - damage
  const newHp = isFinalHit ? phaseFullHp(boss, boss.executesTaken + 1) : Math.max(1, rawHp)
  const lethal = isFinalHit && newHp <= 0
  // 最終ヒット = 致命1回の完遂 = 部屋が1つ閉じる。 executesTaken はフェーズ解禁の燃料。
  let bossNext: Boss = { ...boss, hp: newHp,
    executesTaken: isFinalHit ? boss.executesTaken + 1 : boss.executesTaken }
  // 体幹と体力の基準の比較版 (DEV ?econBase・src/econ_baseline.ts) だけ: 次の幕の体幹の量へ差し替える。配布版は actMaxPostures が無い = 何もしない。
  if (isFinalHit && boss.actMaxPostures) {
    bossNext = { ...bossNext, maxPosture: boss.actMaxPostures[Math.min(bossNext.executesTaken, boss.actMaxPostures.length - 1)] ?? boss.maxPosture }
  }
  // 最終ヒット: stagger 強制終了 (state を idle に、posture を 0 に戻す)。 途中ヒットは stagger 維持で連撃継続
  if (isFinalHit && bossNext.state === 'staggered') {
    bossNext = {
      ...bossNext,
      state: 'idle',
      stateElapsed: -postExecuteStillSec(player),  // 致命後の静止: 攻撃タイマーを 1100ms 分マイナス起算 (2026-06-25)
      //   ※この値だけが致命後の静止に効く。 通常の攻撃間隔 (idleGapMin/Max・finalRoomTempoMul) には不干渉。
      posture: 0,
      currentAttack: null,
      postExecuteInvincMs: 400,  // 致命完了直後の被弾猶予 (2026-06-25)
      // 崩れ姿を見せる間＋立ち上がる間は足を止める (src/boss_rise.ts・絵のあるボスだけ)
      riseHoldMs: bossRiseHoldMs(bossNext.id, lunaExecLampOn() ? lampExecBossDownMs(player) : 0),
      // 崩しまでに積んだ命中は致命で清算する (2026-09-29): 残すと致命明けに割り込み反撃が溜まっている。
      recentHitAgesMs: [],
    }
    // 連撃3段目の得が入っている時 (DEV ?combo3rd=off 以外 = 配布版の既定): 3段目で長くした戻りの止まりを、崩れ中に凍ったまま次の幕へ持ち越さない。
    //   DEV ?combo3rd=off (combo3rdLockSec()=0) は触らない = 旧のまま。
    if (combo3rdLockSec() > 0) {
      bossNext = { ...bossNext, postureRegenLockSec: Math.min(bossNext.postureRegenLockSec ?? 0, bossPostureGraceSec(boss.id)) }
    }
  }
  out.boss = bossNext
  out.player = { ...player, attackHitConsumed: true }
  out.lethal = lethal

  // 演出: 各ヒットで段階的に派手になる、最終ヒットは最も大きい
  const executeHitId = isFinalHit ? 'execute_hit3' : player.executeHitIndex === 0 ? 'execute_hit1' : 'execute_hit2'
  // 新しい版 (DEV ?lunaFinisher=new・src/luna_finisher.ts): 刺す・押し込む・抜くを1つの動きにする。当たりの時刻・量・止め・揺れ・灯は同じ。
  //   光は刺し傷から (白い丸のにじみ・桃の火花をやめる)・音は体と得物の音だけ (振りの音を重ねない)。
  if (finisherV2On()) {
    const lunaHit: 1 | 2 | 3 = isFinalHit ? 3 : player.executeHitIndex === 0 ? 1 : 2
    const w = execNormalWoundPoint(player, boss, lunaHit)   // 3 版目 = 主人公の剣の先 (v2 = ボスの体の縁から)
    out.sfx = execV2Sfx(player.executeHitIndex)
    out.fx = [
      { kind: 'luna_exec', hit: lunaHit, x: w.x, y: w.y, t: time, dir: w.dir },
      ...(isFinalHit ? [{ kind: 'lamp_snuff' as const, lampIndex: Math.max(1, boss.roomCount) - 1 - boss.executesTaken, t: time }] : []),
    ]
    out.hitStopMs = isFinalHit ? 220 : 110
    out.shake = shakeFrom({ amplitude: isFinalHit ? 11 : 6, durationMs: isFinalHit ? 360 : 180 }, time)
    return out
  }
  // 直した版 (?lunaExec=swap2): 作り直した一突き。白い玉 (execute_flash) は出さず、刺し傷から銀の火花 (普段の致命の新しい版の C「火花の爆ぜ」)。
  //   音は普段の致命の3発目 (止めの重い音 execute は使わない)・止めと揺れも止めより軽く。
  if (thrustTimingOn(player)) {
    const w = execWoundPoint(player, boss)
    out.sfx = ['execute_hit3']
    out.fx = [
      { kind: 'luna_exec', hit: 3, x: w.x, y: w.y, t: time, dir: w.dir },
      { kind: 'lamp_snuff' as const, lampIndex: Math.max(1, boss.roomCount) - 1 - boss.executesTaken, t: time },
    ]
    out.hitStopMs = THRUST.hitStopMs
    out.shake = shakeFrom(THRUST.shake, time)
    return out
  }
  // 配布版 (lamp・10-20 作者の答え): 普段の致命 = 深い一突き (swap3 の重い音と止め)。光は刺し傷から体へ走るひびの奥から常夜燈の光が漏れる
  //   (お手本 b・作者「ひびはひびでいいんだけど、常夜灯の光がもれているような感じに」・src/luna_lamp_leak_fx.ts)。桃の火花と白い玉は出さない。
  if (swap && lunaExecLampOn()) {
    const w = execWoundPoint(player, boss)
    out.sfx = ['execute', 'luna_exec_weight']
    out.fx = [
      // 止めの間も光って見えるよう、光の時計を少し先へ (10-29 作者「刺さった瞬間に光ってほしい」)。
      //   両手で刺して引き抜く版 (配布版・指定なし) は刺す時に出さず、抜く時に撃破と同じ弾け (render.ts が主人公の時計から描く・11-04 作者)。
      ...(pullTimingOn(player) ? [] : [{ kind: 'luna_crack' as const, x: w.x, y: w.y, t: time - lampExecLightLeadSec(), dir: w.dir }]),
      { kind: 'lamp_snuff' as const, lampIndex: Math.max(1, boss.roomCount) - 1 - boss.executesTaken, t: time },
    ]
    out.hitStopMs = pullTimingOn(player) ? PULL.hitStopMs : 190
    out.shake = shakeFrom({ amplitude: 13, durationMs: 380 }, time)
    return out
  }
  // 入れ替えの比較: 普段の致命 = 今の止めの深い刺し。音も今の止めの音 (execute)・灯を1つ消すのは今と同じ。
  if (swap) {
    // 重みを足した版 (?lunaExec=swap3・作者 10-20 Q2「突きの重さが足りない」): 動きと光はそのまま、音に低い芯を足して止めを長く。光はお手本を選んでから。
    const heavy = lunaExecSwap3On()
    out.sfx = heavy ? ['execute', 'luna_exec_weight'] : ['execute']
    out.fx = [
      { kind: 'hit_spark', x: fxPos.x, y: fxPos.y, t: time, color: 'pink' },
      { kind: 'execute_flash', x: fxPos.x, y: fxPos.y, t: time },
      { kind: 'lamp_snuff' as const, lampIndex: Math.max(1, boss.roomCount) - 1 - boss.executesTaken, t: time },
    ]
    out.hitStopMs = heavy ? 190 : EXECUTE_FX.finisherHitStopMs
    out.shake = shakeFrom(heavy ? { amplitude: 13, durationMs: 380 } : { amplitude: 12, durationMs: 340 }, time)
    return out
  }
  out.sfx = [executeHitId, 'attack_swing']
  out.fx = [
    { kind: 'hit_spark', x: fxPos.x, y: fxPos.y, t: time, color: 'pink' },
    isFinalHit
      ? { kind: 'execute_flash', x: fxPos.x, y: fxPos.y, t: time }
      : { kind: 'counter_burst', x: fxPos.x, y: fxPos.y, t: time },
    // 致命完遂=部屋1つ閉じる → 描画と同じ右端からindex。描画indexと一致させる (D2-fix 2026-06-15)
    ...(isFinalHit ? [{ kind: 'lamp_snuff' as const, lampIndex: Math.max(1, boss.roomCount) - 1 - boss.executesTaken, t: time }] : []),
  ]
  out.hitStopMs = isFinalHit ? 220 : 110
  out.shake = shakeFrom(
    { amplitude: isFinalHit ? 11 : 6, durationMs: isFinalHit ? 360 : 180 },
    time,
  )
  return out
}

// 2026-06-11 hit-timeline: resolveBossAttack (一発きりの距離判定・hitConsumed 自己消費) は退役。
//   ボス→プレイヤーの全判定は src/hits.ts の updateHits (持続するヒット) が担う。
// Phase 4.10で削除: 処刑(OHK)は廃止。staggered は resolvePlayerAttack の isOpen 側へ統合された。
// 🔴 「倍率ダメージ」ではない: 倍率は掛からず、 econ() の別フィールド (bossHpDamageOpen /
//   bossPostureGainOpen) を使うだけ。
